import { createFileRoute } from '@tanstack/react-router';
import { GuideEmailGate } from '@/components/sections/guide-email-gate';
import { Navbar } from '@/components/sections/navbar';
import { SiteFooter } from '@/components/sections/site-footer';
import { Card } from '@/components/ui/card';
import { Chip } from '@/components/ui/chip';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useLang } from '@/lib/LanguageContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';

export function RessourcesPage() {
  const { lang } = useLang();
  const text = ta(t.ressources, lang);
  const carnetText = ta(t.carnet, lang);

  const articles = [
    { title: carnetText.a1_title, excerpt: carnetText.a1_excerpt },
    { title: carnetText.a2_title, excerpt: carnetText.a2_excerpt }
  ];

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background text-foreground">
      <Navbar />
      <main className="flex-grow py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          {/* Header */}
          <div className="mb-12 text-center md:mb-16">
            <ScrollReveal as="span" className="mb-4 block font-display text-base italic tracking-wide text-secondary">
              {text.eyebrow}
            </ScrollReveal>
            <h1 className="mb-6 font-display text-3xl leading-[1.2] text-balance text-foreground md:text-5xl">
              {text.title}
            </h1>
            <ScrollReveal delay={200} as="p" className="mx-auto max-w-2xl font-sans text-base text-pretty hyphens-auto text-muted-foreground">
              {text.intro}
            </ScrollReveal>
          </div>

          {/* Guide AMG featured */}
          <ScrollReveal>
          <Card variant="default" className="mb-16 border-t-2 border-t-primary">
            <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0 flex-1">
                <div className="mb-3">
                  <Chip tone="peach">{text.guide_featured_label}</Chip>
                </div>
                <h2 className="mb-3 font-display text-2xl leading-[1.25] text-balance text-foreground md:text-3xl">
                  {text.guide_title}
                </h2>
                <p className="font-sans text-base leading-relaxed text-pretty hyphens-auto text-muted-foreground">
                  {text.guide_subtitle}
                </p>
              </div>
              <div className="w-full shrink-0 md:w-72">
                <GuideEmailGate />
              </div>
            </div>
          </Card>
          </ScrollReveal>

          {/* Articles section */}
          <section className="mb-16">
            <ScrollReveal className="mb-8 flex items-baseline justify-between">
              <h2 className="font-display text-2xl text-balance text-foreground md:text-3xl">
                {text.articles_section_title}
              </h2>
              <a
                href="/carnet"
                className="font-sans text-sm text-primary hover:text-primary/80"
              >
                {text.articles_view_all}
              </a>
            </ScrollReveal>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {articles.map((article, i) => (
                <ScrollReveal
                  key={i}
                  delay={(Math.min(i * 100, 400)) as 0 | 100 | 200 | 300 | 400}
                >
                  <Card>
                    <div className="mb-3">
                      <Chip tone="peach">{carnetText.status_coming}</Chip>
                    </div>
                    <h3 className="mb-3 font-display text-xl leading-[1.25] text-balance text-foreground">
                      {article.title}
                    </h3>
                    <p className="font-sans text-sm leading-relaxed text-pretty hyphens-auto text-muted-foreground">
                      {article.excerpt}
                    </p>
                  </Card>
                </ScrollReveal>
              ))}
            </div>
          </section>

          {/* Témoignages section */}
          <ScrollReveal as="section" className="mb-16">
            <h2 className="mb-3 font-display text-2xl text-balance text-foreground md:text-3xl">
              {text.testimonials_section_title}
            </h2>
            <p className="mb-4 font-sans text-base text-pretty hyphens-auto text-muted-foreground">
              {text.testimonials_intro}
            </p>
            <a
              href="/#temoignages"
              className="inline-block font-sans text-sm text-primary hover:text-primary/80"
            >
              {text.testimonials_cta}
            </a>
          </ScrollReveal>

          {/* Final CTA */}
          <ScrollReveal className="text-center">
            <h2 className="mb-6 font-display text-2xl text-balance text-foreground md:text-3xl">
              {text.final_section_title}
            </h2>
            <a
              href="/#contact"
              className="shadow-warm-soft hover:shadow-warm-md inline-flex items-center justify-center rounded-md bg-primary px-8 py-4 font-sans text-sm font-bold uppercase tracking-widest text-primary-foreground transition-all hover:bg-primary/90"
            >
              {text.final_cta}
            </a>
          </ScrollReveal>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

export const Route = createFileRoute('/ressources')({
  component: RessourcesPage
});
